/**
 * Opens an SSO authentication popup window and polls for the JWT token.
 *
 * Three mechanisms retrieve the JWT from the popup:
 *   1. Same-origin URL monitoring (/oauth-success with ?jwt= param)
 *   2. postMessage from the popup's OAuthSuccess page
 *   3. localStorage fallback (sso_pending_jwt key, for COOP-blocked scenarios)
 *
 * If the popup is blocked, falls back to a full-page redirect.
 */
export function openSsoWindow(
  ssoURL: string,
  onJwt: (jwt: string) => void,
): void {
  const ssoResponseUri = /\/oauth-success\/([A-Za-z0-9+=\-._]+)\/?/;

  const ssoWinMaybe = window.open(
    ssoURL,
    'Aegix Login',
    'rel=opener,directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,height=480,width=640',
  );

  if (ssoWinMaybe === null) {
    // Popup blocked — fall back to full-page redirect
    window.location.href = ssoURL;
    return;
  }

  const ssoWin = ssoWinMaybe;

  let intervalCleared = false;

  function cleanup() {
    if (!intervalCleared) {
      clearInterval(interval);
      intervalCleared = true;
    }
    window.removeEventListener('message', handleOAuthSuccess);
  }

  let lastCheck = Date.now();

  const interval = setInterval(() => {
    try {
      if (ssoWin.closed) {
        // Check localStorage one last time before cleanup
        const pendingJwt = localStorage.getItem('sso_pending_jwt');
        const jwtTimestamp = localStorage.getItem('sso_jwt_timestamp');

        if (pendingJwt && jwtTimestamp) {
          const timestamp = parseInt(jwtTimestamp, 10);
          if (Date.now() - timestamp < 10000) {
            localStorage.removeItem('sso_pending_jwt');
            localStorage.removeItem('sso_jwt_timestamp');
            cleanup();
            onJwt(pendingJwt);
            return;
          }
        }

        cleanup();
        return;
      }

      // Try to access popup URL (works if same-origin)
      const href = ssoWin.location.href;
      const searchParams = ssoWin.location.search;

      // Check for /oauth-success/ path pattern
      if (href && href.includes('/oauth-success')) {
        const match = ssoResponseUri.exec(href);
        const token = match ? match[1] : null;
        if (token) {
          cleanup();
          onJwt(token);
          ssoWin.close();
          return;
        }

        // Check for ?jwt= query param
        if (searchParams && searchParams.includes('jwt=')) {
          const urlParams = new URLSearchParams(searchParams);
          const jwtParam = urlParams.get('jwt');
          if (jwtParam) {
            cleanup();
            onJwt(jwtParam);
            ssoWin.close();
            return;
          }
        }
      }
    } catch {
      // Cross-origin access blocked — check localStorage fallback
      const pendingJwt = localStorage.getItem('sso_pending_jwt');
      const jwtTimestamp = localStorage.getItem('sso_jwt_timestamp');

      if (pendingJwt && jwtTimestamp) {
        const timestamp = parseInt(jwtTimestamp, 10);
        if (timestamp > lastCheck) {
          localStorage.removeItem('sso_pending_jwt');
          localStorage.removeItem('sso_jwt_timestamp');
          cleanup();
          onJwt(pendingJwt);
          ssoWin.close();
          return;
        }
      }
      lastCheck = Date.now();
    }
  }, 500);

  function handleOAuthSuccess(event: MessageEvent) {
    if (event.source !== ssoWin) return;

    // Validate origin is from an allowed domain
    const allowedDomains = [
      'aegix.global',
      'localhost',
      'dirsalert.com',
      'dirs.app',
    ];
    try {
      const messageOriginDomain = new URL(event.origin).hostname
        .split('.')
        .slice(-2)
        .join('.');
      if (!allowedDomains.includes(messageOriginDomain)) return;
    } catch {
      return;
    }

    try {
      let token: string | null = null;

      if (typeof event.data === 'string') {
        try {
          const eventData = JSON.parse(event.data);
          if (eventData.type === 'oauth-success' && eventData.token) {
            token = eventData.token;
          } else if (eventData.jwt) {
            token = eventData.jwt;
          }
        } catch {
          if (event.data && event.data.length > 20) {
            token = event.data;
          }
        }
      } else if (event.data && typeof event.data === 'object') {
        token = event.data.token || event.data.jwt || null;
      }

      if (token) {
        cleanup();
        onJwt(token);
        try {
          ssoWin.close();
        } catch {
          // ignore
        }
      }
    } catch {
      // ignore
    }
  }

  window.addEventListener('message', handleOAuthSuccess);
}
